<script setup>
import { provideSidebarContext } from './provider';
import { useMapGetter } from 'dashboard/composables/store';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { getContentSidebar } from './contentSidebar';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'changeNavbar',
]);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);
const menuItems = getContentSidebar();

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});
const globalConfig = useMapGetter('globalConfig/get');
</script>

<template>
  <aside
    class="w-[70px] bg-n-navbar-background rounded-2xl rtl:border-l ltr:border-r border-n-weak h-full flex flex-col text-sm pb-1 py-4"
  >
    <div class="flex justify-end p-1">
      <button type="button" @click="emit('changeNavbar')">
        <Icon
          icon="i-lucide-arrow-right-from-line"
          class="size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </div>
    <section class="grid gap-4 px-3 mb-4">
      <div class="mx-auto">
        <img
          :src="globalConfig.logoThumbnail"
          :alt="globalConfig.installationName"
          class="block object-contain w-auto h-12 mx-auto"
        />
      </div>
      <SidebarAccountSwitcher
        class="flex-grow min-w-0 -mx-1"
        :show-label="false"
        @show-create-account-modal="emit('showCreateAccountModal')"
      />
      <RouterLink
        :to="{ name: 'search' }"
        class="router-link-active router-link-exact-active flex items-center justify-center w-full px-2 py-1 h-10 shadow-md bg-white rounded-md"
      >
        <span class="flex-shrink-0 i-lucide-search size-4 text-n-brand" />
      </RouterLink>
      <ComposeConversation align-position="right">
        <template #trigger="{ toggle }">
          <Button
            icon="i-lucide-plus size-6"
            color="blue"
            variant="solid"
            size="md"
            class="bg-n-bran w-full"
            @click="toggle"
          />
        </template>
      </ComposeConversation>
    </section>
    <nav class="grid flex-grow gap-2 pb-5 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
          :show-sidebar-group-label="false"
        />
      </ul>
    </nav>
    <section
      class="py-1 px-4 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-center gap-2 items-center"
    >
      <SidebarProfileMenu
        :is-expanded-sidebar="false"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </section>
  </aside>
</template>
